import { Helmet } from 'react-helmet-async';
import type { VenueResponse, Amenities } from '@lov/shared';

interface VenueSEOProps {
  venue: VenueResponse;
}

function safeJsonLd(data: unknown): string {
  return JSON.stringify(data)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

const SCHEMA_TYPE_MAP: Record<string, string | string[]> = {
  GAMING: ['EntertainmentBusiness', 'SportsActivityLocation'],
  SNOOKER: ['EntertainmentBusiness', 'SportsClub'],
  KIDS_PLAYAREA: ['EntertainmentBusiness', 'AmusementPark'],
};

const OG_FALLBACK_MAP: Record<string, string> = {
  GAMING: '/og-gaming.png',
  SNOOKER: '/og-snooker.png',
  KIDS_PLAYAREA: '/og-kids.png',
};

const DAY_OF_WEEK_MAP: Record<string, string> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
  sunday: 'Sunday',
};

function buildOpeningHours(operatingHours: unknown): object[] {
  if (!operatingHours || typeof operatingHours !== 'object') return [];
  const hours = operatingHours as Record<string, unknown>;
  if (Object.keys(hours).length === 0) return [];

  const HH_MM = /^\d{2}:\d{2}$/;
  const specs: object[] = [];
  for (const [day, dayHours] of Object.entries(hours)) {
    const schemaDay = DAY_OF_WEEK_MAP[day];
    if (!schemaDay || !dayHours || typeof dayHours !== 'object') continue;
    const h = dayHours as Record<string, unknown>;
    if (h.closed === true) continue;
    // Handle is24Hours: true (open/close may be absent)
    if (h.is24Hours === true) {
      specs.push({
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: `https://schema.org/${schemaDay}`,
        opens: '00:00',
        closes: '24:00',
      });
      continue;
    }
    if (!h.open || !h.close) continue;
    if (!HH_MM.test(String(h.open)) || !HH_MM.test(String(h.close))) continue;

    // schema.org uses "24:00" for midnight-next-day
    const closeTime = h.close === '00:00' ? '24:00' : String(h.close);

    specs.push({
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: `https://schema.org/${schemaDay}`,
      opens: String(h.open),
      closes: closeTime,
    });
  }
  return specs;
}

export function VenueSEO({ venue }: VenueSEOProps) {
  const title =
    `${venue.name} in ${venue.locality || ''} ${venue.city?.name || ''} | Lord of Venues`
      .replace(/\s+/g, ' ')
      .trim();

  // Build amenities list
  const amenitiesLabels: Record<keyof Amenities, string> = {
    wifi: 'WiFi',
    parking: 'Parking',
    airConditioning: 'AC',
    wheelchairAccessible: 'Wheelchair Access',
    restrooms: 'Restrooms',
    foodAndBeverages: 'Food & Drinks',
    lockers: 'Lockers',
    waitingArea: 'Waiting Area',
    gaming: 'Gaming',
  };

  const activeAmenitiesArr = Object.entries(venue.amenities || {})
    .filter(([, value]) => value === true)
    .map(([key]) => amenitiesLabels[key as keyof Amenities])
    .slice(0, 4);
  const activeAmenities = activeAmenitiesArr.join(', ');

  const rating = venue.averageRating ? `${venue.averageRating}★` : '';
  const reviews = venue.totalReviews ? `(${venue.totalReviews} reviews)` : '';
  const price = venue.basePrice ? `From ₹${venue.basePrice}/hr.` : '';

  const description =
    `Book ${venue.name} in ${venue.locality || ''}, ${venue.city?.name || ''}. ${activeAmenities}. ${price} ${rating} ${reviews}`
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 155);

  const canonicalUrl = venue.seoUrl
    ? `https://lordofvenues.com${venue.seoUrl}`
    : `https://lordofvenues.com/venues/${venue.slug}`;

  const venueTypeFallback = venue.venueType
    ? (OG_FALLBACK_MAP[venue.venueType] ?? '/og-image.png')
    : '/og-image.png';
  const firstImage =
    venue.images && venue.images[0]
      ? venue.images[0].startsWith('http')
        ? venue.images[0]
        : `https://lordofvenues.com${venue.images[0]}`
      : null;
  const imageUrl =
    venue.coverImage && venue.coverImage !== ''
      ? venue.coverImage
      : firstImage
        ? firstImage
        : `https://lordofvenues.com${venueTypeFallback}`;

  const schemaType = venue.venueType
    ? (SCHEMA_TYPE_MAP[venue.venueType] ?? 'EntertainmentBusiness')
    : 'EntertainmentBusiness';

  const openingHoursSpec = buildOpeningHours(venue.operatingHours);

  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@id': canonicalUrl,
    '@type': schemaType,
    name: venue.name,
    description: venue.description,
    image: [imageUrl, ...(venue.images?.filter(Boolean).slice(0, 3) ?? [])].filter(Boolean),
    address: {
      '@type': 'PostalAddress',
      streetAddress: venue.address,
      addressLocality: venue.city?.name,
      addressRegion: venue.state?.name,
      postalCode: venue.pincode,
      addressCountry: 'IN',
    },
    ...(venue.latitude &&
      venue.longitude &&
      venue.latitude !== 0 &&
      venue.longitude !== 0 && {
        geo: {
          '@type': 'GeoCoordinates',
          latitude: venue.latitude,
          longitude: venue.longitude,
        },
      }),
    url: canonicalUrl,
    ...(venue.vendor?.instagram && {
      sameAs: [`https://www.instagram.com/${venue.vendor.instagram.replace(/^@/, '')}/`],
    }),
    ...(venue.googleMapsLink && { hasMap: venue.googleMapsLink }),
    ...(activeAmenitiesArr.length > 0 && {
      amenityFeature: activeAmenitiesArr.map((name) => ({
        '@type': 'LocationFeatureSpecification',
        name,
        value: true,
      })),
    }),
    priceRange: venue.basePrice
      ? venue.basePrice < 500
        ? '₹'
        : venue.basePrice < 1500
          ? '₹₹'
          : '₹₹₹'
      : '₹₹',
    ...(venue.averageRating &&
      venue.totalReviews &&
      venue.totalReviews > 0 && {
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: venue.averageRating.toString(),
          reviewCount: venue.totalReviews.toString(),
        },
      }),
    ...(openingHoursSpec.length > 0 && { openingHoursSpecification: openingHoursSpec }),
  };

  // Map backend venueType values to URL slugs
  const venueTypeToSlug: Record<string, string> = {
    GAMING: 'gaming',
    SNOOKER: 'snooker',
    KIDS_PLAYAREA: 'kids-play',
  };
  const venueTypeSlug = venue.venueType ? (venueTypeToSlug[venue.venueType] ?? null) : null;
  const citySlug = venue.city?.slug ?? null;

  // Build breadcrumb items
  const breadcrumbItems: Array<{ '@type': string; position: number; name: string; item?: string }> =
    [{ '@type': 'ListItem', position: 1, name: 'Home', item: 'https://lordofvenues.com' }];

  if (venueTypeSlug && citySlug) {
    const cityVenueTypeLabel =
      venue.venueType === 'GAMING'
        ? 'Gaming'
        : venue.venueType === 'SNOOKER'
          ? 'Snooker'
          : 'Kids Play Area';
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: `${cityVenueTypeLabel} in ${venue.city?.name ?? citySlug}`,
      item: `https://lordofvenues.com/discover/${venueTypeSlug}/${encodeURIComponent(citySlug)}`,
    });
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 3,
      name: venue.name,
    });
  } else {
    breadcrumbItems.push({
      '@type': 'ListItem',
      position: 2,
      name: venue.name,
    });
  }

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems,
  };

  // FAQ schema — ordered by conversion intent (price first)
  const amenitiesText = activeAmenities || 'various amenities';
  const hoursAnswer =
    openingHoursSpec.length > 0
      ? `Operating hours vary by day. Check the venue listing for exact times.`
      : `Please check the venue listing for operating hours, as they vary by day.`;

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `How much does it cost to book ${venue.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: venue.basePrice
            ? `Booking starts from ₹${venue.basePrice} per hour. All prices shown are per hour with no hidden charges.`
            : `Pricing varies by time slot and duration. All prices are shown upfront with no hidden charges.`,
        },
      },
      {
        '@type': 'Question',
        name: `What amenities are available at ${venue.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${venue.name} offers ${amenitiesText}. Full amenity details are available on the venue listing page.`,
        },
      },
      {
        '@type': 'Question',
        name: `What are the operating hours of ${venue.name}?`,
        acceptedAnswer: { '@type': 'Answer', text: hoursAnswer },
      },
      {
        '@type': 'Question',
        name: `How do I book ${venue.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `Click the Book Now button on this page, select your preferred date and time slot, and complete the payment. Bookings are confirmed instantly.`,
        },
      },
      {
        '@type': 'Question',
        name: `What is the cancellation policy for ${venue.name}?`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${venue.name} offers flexible cancellations. Refunds are processed to your wallet within 24-48 hours of cancellation.`,
        },
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content="business.business" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={`${venue.name} - ${venue.city?.name || ''}`} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />

      {/* Crawl directives */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1" />

      {/* Open Graph extras */}
      <meta property="og:locale" content="en_IN" />
      <meta property="og:site_name" content="Lord of Venues" />

      {/* Twitter image alt */}
      <meta name="twitter:image:alt" content={`${venue.name} - ${venue.city?.name || ''}`} />

      {/* Article freshness */}
      {venue.updatedAt && <meta property="article:modified_time" content={venue.updatedAt} />}

      {/* Geo meta tags */}
      {venue.latitude && venue.longitude && venue.latitude !== 0 && venue.longitude !== 0 && (
        <>
          <meta name="geo.position" content={`${venue.latitude};${venue.longitude}`} />
          <meta name="ICBM" content={`${venue.latitude}, ${venue.longitude}`} />
        </>
      )}
      {venue.city?.name && (
        <meta
          name="geo.placename"
          content={[venue.locality, venue.city.name].filter(Boolean).join(', ')}
        />
      )}
      {venue.state?.code && <meta name="geo.region" content={`IN-${venue.state.code}`} />}

      {/* Keywords */}
      <meta
        name="keywords"
        content={[
          venue.name,
          venue.venueType?.toLowerCase(),
          `book ${venue.venueType?.toLowerCase()} in ${venue.city?.name || ''}`,
          venue.city?.name,
          venue.state?.name,
          venue.locality,
          'hourly booking',
          'venue booking',
        ]
          .filter(Boolean)
          .join(', ')}
      />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">{safeJsonLd(jsonLd)}</script>
      <script type="application/ld+json">{safeJsonLd(breadcrumbJsonLd)}</script>
      <script type="application/ld+json">{safeJsonLd(faqJsonLd)}</script>
    </Helmet>
  );
}
